<script setup>
import { computed, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const [showActionsDropdown, toggleDropdown] = useToggle();

const activeHoursEnabled = ref(props.inbox.active_hours_enabled || false);
const activeHoursStart = ref(props.inbox.active_hours_start || '08:00');
const activeHoursEnd = ref(props.inbox.active_hours_end || '18:00');

watch(
  () => props.inbox,
  newInbox => {
    activeHoursEnabled.value = newInbox.active_hours_enabled || false;
    activeHoursStart.value = newInbox.active_hours_start || '08:00';
    activeHoursEnd.value = newInbox.active_hours_end || '18:00';
  }
);

const saveActiveHours = () => {
  store.dispatch('captainInboxes/updateActiveHours', {
    assistantId: route.params.assistantId,
    inboxId: props.id,
    active_hours_enabled: activeHoursEnabled.value,
    active_hours_start: activeHoursStart.value,
    active_hours_end: activeHoursEnd.value,
  });
};

const inboxName = computed(() => {
  const inbox = props.inbox;
  if (!inbox?.name) {
    return '';
  }

  const isTwilioChannel = inbox.channel_type === INBOX_TYPES.TWILIO;
  const isWhatsAppChannel = inbox.channel_type === INBOX_TYPES.WHATSAPP;
  const isEmailChannel = inbox.channel_type === INBOX_TYPES.EMAIL;

  if (isTwilioChannel || isWhatsAppChannel) {
    const identifier = inbox.messaging_service_sid || inbox.phone_number;
    return identifier ? `${inbox.name} (${identifier})` : inbox.name;
  }

  if (isEmailChannel && inbox.email) {
    return `${inbox.name} (${inbox.email})`;
  }

  return inbox.name;
});

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.INBOXES.OPTIONS.DISCONNECT'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const icon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'outline', voiceEnabled);
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-col w-full gap-3">
      <div class="flex justify-between w-full gap-1">
        <span
          class="text-base text-n-slate-12 line-clamp-1 flex items-center gap-2"
        >
          <span :class="icon" />
          {{ inboxName }}
        </span>
        <div class="flex items-center gap-2">
          <Policy
            v-on-clickaway="() => toggleDropdown(false)"
            :permissions="['administrator']"
            class="relative flex items-center group"
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:left-0 top-full"
              @action="handleAction($event)"
            />
          </Policy>
        </div>
      </div>

      <div class="flex items-center gap-3 flex-wrap">
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input
            v-model="activeHoursEnabled"
            type="checkbox"
            class="size-4 rounded border-n-weak accent-n-brand"
            @change="saveActiveHours"
          />
          {{ $t('CAPTAIN.INBOXES.ACTIVE_HOURS.LABEL') }}
        </label>

        <div v-if="activeHoursEnabled" class="flex items-center gap-2">
          <input
            v-model="activeHoursStart"
            type="time"
            class="px-2 py-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="saveActiveHours"
          />
          <span class="text-sm text-n-slate-11">
            {{ $t('CAPTAIN.INBOXES.ACTIVE_HOURS.TO') }}
          </span>
          <input
            v-model="activeHoursEnd"
            type="time"
            class="px-2 py-1 text-sm border rounded border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="saveActiveHours"
          />
        </div>
      </div>
    </div>
  </CardLayout>
</template>
